import Map from "../components/Map";
import styled from "styled-components";
import NavBar from "../components/Navbar";
import PhotoGallery from "../components/PhotoGallery";
import LeftRightSlideContainer from "../components/LeftRightSlideContainer";
import houses from "./HouseInfo";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { FaArrowUp } from "react-icons/fa";

import { PrimaryButton } from "../styledComponents";

const HomeContainer = styled.div`
  height: auto;
  width: 100%;
`;

const MapSection = styled.section`
  background-color: ${({ theme }) => theme.backgroundColor};
  padding-top: 3rem;
  text-align: center;
`;

const MapSectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const MapSectionBlurb = styled.p`
  font-family: ${({ theme }) => theme.fonts.primary};
  color: ${({ theme }) => theme.textColors.secondary};
  margin-top: 0;
  margin-bottom: 1.5rem;
`;

const ScrollToTopButton = styled.button<{ $visible: boolean }>`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: none;
  background-color: ${({ theme }) => theme.backgroundColors.secondary};
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  transition: opacity 0.3s ease;
  z-index: 100;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  }
`;

const WelcomeContainer = styled.div`
  height: 95vh;
  background-color: ${({ theme }) => theme.backgroundColor};
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center; // Centers the image horizontally
  align-items: center; // Centers the image vertically

  img {
    width: 700px; // Maximum width of the image is 80% of its container
    height: 500px; // Maximum height of the image is 80% of its container
    object-fit: cover; // Ensures the image does not get distorted
  }

  @media (max-width: 768px) {
    img {
      width: 100%;
      height: 100%;
    }
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

type InfoContainerProps = {
  alignRight?: boolean;
};

const InfoContainer = styled.div<InfoContainerProps>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 2rem;

  flex-direction: column;
  h1,
  h2,
  p {
    text-align: ${(props) => (props.alignRight ? "right" : "left")};
    width: 100%;
  }

  @media (max-width: 768px) {
    padding: 2rem;
    p,
    h1 {
      text-align: left;
    }
  }
`;

const Header = styled.h1`
  font-family: ${({ theme }) => theme.fonts.primary};
  margin-bottom: 0rem;
`;

const SubTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.primary};
  color: ${({ theme }) => theme.textColors.secondary};
  margin-top: 0rem;
  font-size: 1rem;
`;

const BodyText = styled.p`
  font-family: ${({ theme }) => theme.fonts.primary};
`;

type HouseInfoProps = {
  tagline: string;
  description: string;
  alignRight?: boolean; // Add this line
  route: string;
  propertyId: string;
};

const HouseInfo: React.FC<HouseInfoProps> = ({
  tagline,
  description,
  alignRight,
  route,
  propertyId,
}) => {
  const [, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup the event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <InfoContainer alignRight={alignRight}>
      <Header>{tagline}</Header>
      <SubTitle>Property ID: {propertyId}</SubTitle>
      <BodyText>{description}</BodyText>

      <StyledLink to={route}>
        <PrimaryButton color="#000">Booking Details</PrimaryButton>
      </StyledLink>
    </InfoContainer>
  );
};

type FlagPhotoProps = {
  src: string;
  alt: string;
};

function FlagPhoto(props: FlagPhotoProps) {
  const { src, alt } = props;
  return (
    <ImageContainer>
      <img src={src} alt={alt} loading="lazy" />
    </ImageContainer>
  );
}

function Home() {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup the event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <HomeContainer>
      <NavBar />

      <WelcomeContainer>
        <PhotoGallery />
      </WelcomeContainer>

      {windowWidth > 768
        ? houses.map((house, index) =>
            index % 2 === 0 ? (
              <LeftRightSlideContainer
                key={house.propertyId}
                LeftSide={HouseInfo({
                  tagline: house.tagline,
                  description: house.description,
                  alignRight: true,
                  route: house.route,
                  propertyId: house.propertyId,
                })}
                RightSide={FlagPhoto({ src: house.flagPhoto, alt: house.tagline })}
              />
            ) : (
              <LeftRightSlideContainer
                key={house.propertyId}
                LeftSide={FlagPhoto({ src: house.flagPhoto, alt: house.tagline })}
                RightSide={HouseInfo({
                  tagline: house.tagline,
                  description: house.description,
                  alignRight: false,
                  route: house.route,
                  propertyId: house.propertyId,
                })}
              />
            ),
          )
        : houses.map((house) => (
            <LeftRightSlideContainer
              key={house.propertyId}
              LeftSide={HouseInfo({
                tagline: house.tagline,
                description: house.description,
                alignRight: false,
                route: house.route,
                propertyId: house.propertyId,
              })}
              RightSide={FlagPhoto({ src: house.flagPhoto, alt: house.tagline })}
            />
          ))}

      <MapSection>
        <MapSectionTitle>Find Our Properties</MapSectionTitle>
        <MapSectionBlurb>
          All of our rentals are located along the shores of Cape Cod. Click a
          pin to learn more about each property.
        </MapSectionBlurb>
        <Map />
      </MapSection>

      <ScrollToTopButton
        $visible={showScrollTop}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Scroll to top"
      >
        <FaArrowUp />
      </ScrollToTopButton>
    </HomeContainer>
  );
}

export default Home;
